import { useEffect, useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import {
  getBookings,
  createBooking,
  updateBooking,
  cancelBooking,
  deleteBooking,
  bulkUpdateBookings,
  getClients,
  type Booking,
  type Client,
} from '../../../services/api'

const springConfig = { type: "spring", stiffness: 60, damping: 10 }

export default function DashboardBookings() {
  const [loading, setLoading] = useState(true)
  const [bookings, setBookings] = useState<(Booking & { clients?: { name: string; email: string } })[]>([])
  const [clients, setClients] = useState<Client[]>([])
  const [selectedBookings, setSelectedBookings] = useState<Set<string>>(new Set())
  const [isFormOpen, setIsFormOpen] = useState(false)
  const [editingBooking, setEditingBooking] = useState<Booking | null>(null)
  const [saving, setSaving] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState<string>('all')
  const [serviceTypeFilter, setServiceTypeFilter] = useState<string>('all')
  const [formData, setFormData] = useState({
    client_id: '',
    payment_id: '',
    service_type: 'tutoring' as 'tutoring' | 'web-dev',
    service_title: '',
    status: 'scheduled' as 'scheduled' | 'confirmed' | 'completed' | 'canceled' | 'no-show',
    scheduled_start: '',
    scheduled_end: '',
    timezone: 'UTC',
    meeting_url: '',
    notes: '',
  })

  useEffect(() => {
    loadData()
  }, [statusFilter, serviceTypeFilter])

  useEffect(() => {
    loadClients()
  }, [])

  const loadData = async () => {
    setLoading(true)
    try {
      const filters: any = {}
      if (statusFilter !== 'all') filters.status = statusFilter
      if (serviceTypeFilter !== 'all') filters.service_type = serviceTypeFilter

      const data = await getBookings(filters)
      setBookings(data as any)
    } catch (error) {
      console.error('Error loading bookings:', error)
    } finally {
      setLoading(false)
    }
  }

  const loadClients = async () => {
    const data = await getClients()
    setClients(data)
  }

  const handleCreate = () => {
    setEditingBooking(null)
    setFormData({
      client_id: '',
      payment_id: '',
      service_type: 'tutoring',
      service_title: '',
      status: 'scheduled',
      scheduled_start: '',
      scheduled_end: '',
      timezone: 'UTC',
      meeting_url: '',
      notes: '',
    })
    setIsFormOpen(true)
  }

  const handleEdit = (booking: Booking) => {
    setEditingBooking(booking)
    const startDate = booking.scheduled_start ? new Date(booking.scheduled_start).toISOString().slice(0, 16) : ''
    const endDate = booking.scheduled_end ? new Date(booking.scheduled_end).toISOString().slice(0, 16) : ''
    setFormData({
      client_id: booking.client_id,
      payment_id: booking.payment_id || '',
      service_type: booking.service_type,
      service_title: booking.service_title || '',
      status: booking.status,
      scheduled_start: startDate,
      scheduled_end: endDate,
      timezone: booking.timezone || 'UTC',
      meeting_url: booking.meeting_url || '',
      notes: booking.notes || '',
    })
    setIsFormOpen(true)
  }

  const handleSave = async () => {
    if (!formData.client_id || !formData.scheduled_start || !formData.scheduled_end) {
      alert('Please fill in required fields (Client, Start Date, End Date)')
      return
    }

    setSaving(true)
    try {
      const bookingData: any = {
        ...formData,
        scheduled_start: new Date(formData.scheduled_start).toISOString(),
        scheduled_end: new Date(formData.scheduled_end).toISOString(),
        payment_id: formData.payment_id || null,
      }

      if (editingBooking) {
        await updateBooking(editingBooking.id, bookingData)
      } else {
        await createBooking(bookingData)
      }

      setIsFormOpen(false)
      loadData()
    } catch (error: any) {
      alert(error.message || 'Failed to save booking')
    } finally {
      setSaving(false)
    }
  }

  const handleCancel = async (id: string) => {
    if (!confirm('Are you sure you want to cancel this booking? Session credits will be refunded if applicable.')) return

    try {
      await cancelBooking(id)
      loadData()
      alert('Booking canceled and credits refunded')
    } catch (error) {
      alert('Failed to cancel booking')
    }
  }

  const handleDelete = async (id: string) => {
    if (!confirm('Are you sure you want to delete this booking? This action cannot be undone. Session credits will be refunded if applicable.')) return

    try {
      await deleteBooking(id)
      loadData()
      alert('Booking deleted and credits refunded')
    } catch (error) {
      alert('Failed to delete booking')
    }
  }

  const handleBulkAction = async (action: 'delete' | 'cancel' | 'status', value?: string) => {
    if (selectedBookings.size === 0) {
      alert('Please select at least one booking')
      return
    }

    if (action === 'delete') {
      if (!confirm(`Are you sure you want to delete ${selectedBookings.size} booking(s)? Credits will be refunded.`)) return
      for (const id of selectedBookings) {
        await deleteBooking(id)
      }
    } else if (action === 'cancel') {
      if (!confirm(`Are you sure you want to cancel ${selectedBookings.size} booking(s)? Credits will be refunded.`)) return
      for (const id of selectedBookings) {
        await cancelBooking(id)
      }
    } else if (action === 'status' && value) {
      await bulkUpdateBookings(Array.from(selectedBookings), { status: value as any })
    }

    setSelectedBookings(new Set())
    loadData()
  }

  const toggleSelect = (id: string) => {
    const newSelected = new Set(selectedBookings)
    if (newSelected.has(id)) {
      newSelected.delete(id)
    } else {
      newSelected.add(id)
    }
    setSelectedBookings(newSelected)
  }

  const toggleSelectAll = () => {
    if (selectedBookings.size === filteredBookings.length) {
      setSelectedBookings(new Set())
    } else {
      setSelectedBookings(new Set(filteredBookings.map(b => b.id)))
    }
  }

  const filteredBookings = bookings.filter(booking => {
    const matchesSearch = !searchTerm || 
      booking.service_title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      booking.clients?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      booking.clients?.email?.toLowerCase().includes(searchTerm.toLowerCase())
    return matchesSearch
  })

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    })
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
      case 'confirmed':
        return 'text-green-600 dark:text-green-400'
      case 'scheduled':
        return 'text-yellow-600 dark:text-yellow-400'
      case 'canceled':
      case 'no-show':
        return 'text-red-600 dark:text-red-400'
      default:
        return 'text-text-primary/70'
    }
  }

  if (loading) {
    return (
      <div className="text-center py-12">
        <div className="text-4xl mb-4">⏳</div>
        <p className="text-text-primary/70">Loading bookings...</p>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h3 className="text-xl sm:text-2xl font-semibold text-text-primary mb-1">Bookings</h3>
          <p className="text-sm text-text-primary/70">Manage Calendly appointments and scheduled sessions</p>
        </div>
        <motion.button
          onClick={handleCreate}
          className="px-4 sm:px-6 py-2.5 sm:py-3 neu rounded-xl font-semibold text-text-primary neu-hover bg-accent/10 hover:bg-accent/20 transition-colors"
          whileHover={{ scale: 1.02, y: -1 }}
          whileTap={{ scale: 0.98 }}
          transition={springConfig}
        >
          + New Booking
        </motion.button>
      </div>

      {/* Filters and Search */}
      <div className="neu p-4 rounded-2xl space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <input
            type="text"
            placeholder="Search by service, client name, or email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full px-4 py-2.5 neu rounded-xl text-text-primary bg-background focus:outline-none focus:ring-2 focus:ring-accent/30"
          />
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-4 py-2.5 neu rounded-xl text-text-primary bg-background focus:outline-none focus:ring-2 focus:ring-accent/30"
          >
            <option value="all">All Statuses</option>
            <option value="scheduled">Scheduled</option>
            <option value="confirmed">Confirmed</option>
            <option value="completed">Completed</option>
            <option value="canceled">Canceled</option>
            <option value="no-show">No Show</option>
          </select>
          <select
            value={serviceTypeFilter}
            onChange={(e) => setServiceTypeFilter(e.target.value)}
            className="px-4 py-2.5 neu rounded-xl text-text-primary bg-background focus:outline-none focus:ring-2 focus:ring-accent/30"
          >
            <option value="all">All Services</option>
            <option value="tutoring">Tutoring</option>
            <option value="web-dev">Web Development</option>
          </select>
        </div>

        {/* Bulk Actions */}
        {selectedBookings.size > 0 && (
          <div className="flex items-center gap-2 pt-2 border-t border-shadow-dark/10">
            <span className="text-sm text-text-primary/70">
              {selectedBookings.size} selected
            </span>
            <select
              onChange={(e) => {
                if (e.target.value) {
                  handleBulkAction('status', e.target.value)
                  e.target.value = ''
                }
              }}
              className="px-3 py-1.5 neu rounded-lg text-sm text-text-primary bg-background focus:outline-none"
            >
              <option value="">Bulk Status Update</option>
              <option value="scheduled">Set to Scheduled</option>
              <option value="confirmed">Set to Confirmed</option>
              <option value="completed">Set to Completed</option>
              <option value="canceled">Set to Canceled</option>
            </select>
            <motion.button
              onClick={() => handleBulkAction('cancel')}
              className="px-3 py-1.5 neu rounded-lg text-sm text-yellow-600 hover:bg-yellow-50 dark:hover:bg-yellow-900/20"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              Cancel Selected
            </motion.button>
            <motion.button
              onClick={() => handleBulkAction('delete')}
              className="px-3 py-1.5 neu rounded-lg text-sm text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              Delete Selected
            </motion.button>
          </div>
        )}
      </div>

      {/* Bookings List */}
      {filteredBookings.length === 0 ? (
        <div className="neu p-12 rounded-2xl text-center">
          <p className="text-text-primary/70">No bookings found.</p>
        </div>
      ) : (
        <div className="space-y-4">
          <div className="neu rounded-2xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-background/50">
                  <tr>
                    <th className="px-4 py-3 text-left">
                      <input
                        type="checkbox"
                        checked={selectedBookings.size === filteredBookings.length && filteredBookings.length > 0}
                        onChange={toggleSelectAll}
                        className="neu rounded"
                      />
                    </th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-text-primary">Service</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-text-primary">Client</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-text-primary">Start Time</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-text-primary">End Time</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-text-primary">Status</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-text-primary">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredBookings.map((booking) => (
                    <tr
                      key={booking.id}
                      className="border-t border-shadow-dark/10 hover:bg-background/30"
                    >
                      <td className="px-4 py-3">
                        <input
                          type="checkbox"
                          checked={selectedBookings.has(booking.id)}
                          onChange={() => toggleSelect(booking.id)}
                          className="neu rounded"
                        />
                      </td>
                      <td className="px-4 py-3 text-sm text-text-primary font-medium">
                        {booking.service_title || `${booking.service_type} Service`}
                      </td>
                      <td className="px-4 py-3 text-sm text-text-primary">
                        {booking.clients?.name || booking.clients?.email || 'N/A'}
                      </td>
                      <td className="px-4 py-3 text-sm text-text-primary/70">
                        {formatDate(booking.scheduled_start)}
                      </td>
                      <td className="px-4 py-3 text-sm text-text-primary/70">
                        {formatDate(booking.scheduled_end)}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`text-xs font-medium px-2 py-1 rounded ${getStatusColor(booking.status)}`}>
                          {booking.status}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex gap-2">
                          {booking.meeting_url && (
                            <a
                              href={booking.meeting_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-accent hover:text-accent-secondary text-sm"
                            >
                              Join
                            </a>
                          )}
                          {booking.calendly_event_uri && (
                            <a
                              href={booking.calendly_event_uri}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-blue-600 hover:text-blue-700 text-sm"
                            >
                              Calendly
                            </a>
                          )}
                          <button
                            onClick={() => handleEdit(booking)}
                            className="text-accent hover:text-accent-secondary text-sm"
                          >
                            Edit
                          </button>
                          {booking.status !== 'canceled' && (
                            <button
                              onClick={() => handleCancel(booking.id)}
                              className="text-yellow-600 hover:text-yellow-700 text-sm"
                            >
                              Cancel
                            </button>
                          )}
                          <button
                            onClick={() => handleDelete(booking.id)}
                            className="text-red-600 hover:text-red-700 text-sm"
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Form Modal */}
      <AnimatePresence>
        {isFormOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
            onClick={() => setIsFormOpen(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9 }}
              className="neu rounded-2xl p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <h3 className="text-xl font-semibold mb-4 text-text-primary">
                {editingBooking ? 'Edit Booking' : 'New Booking'}
              </h3>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-2 text-text-primary/70">
                    Client *
                  </label>
                  <select
                    value={formData.client_id}
                    onChange={(e) => setFormData({ ...formData, client_id: e.target.value })}
                    className="w-full px-4 py-2.5 neu rounded-xl text-text-primary bg-background focus:outline-none focus:ring-2 focus:ring-accent/30"
                    required
                  >
                    <option value="">Select a client</option>
                    {clients.map((client) => {
                      const displayName = client.first_name && client.last_name ? `${client.first_name} ${client.last_name}` : client.name || client.email.split('@')[0];
                      return (
                        <option key={client.id} value={client.id}>
                          {displayName} ({client.email})
                        </option>
                      );
                    })}
                  </select>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-2 text-text-primary/70">
                      Service Type
                    </label>
                    <select
                      value={formData.service_type}
                      onChange={(e) => setFormData({ ...formData, service_type: e.target.value as any })}
                      className="w-full px-4 py-2.5 neu rounded-xl text-text-primary bg-background focus:outline-none focus:ring-2 focus:ring-accent/30"
                    >
                      <option value="tutoring">Tutoring</option>
                      <option value="web-dev">Web Development</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2 text-text-primary/70">
                      Status
                    </label>
                    <select
                      value={formData.status}
                      onChange={(e) => setFormData({ ...formData, status: e.target.value as any })}
                      className="w-full px-4 py-2.5 neu rounded-xl text-text-primary bg-background focus:outline-none focus:ring-2 focus:ring-accent/30"
                    >
                      <option value="scheduled">Scheduled</option>
                      <option value="confirmed">Confirmed</option>
                      <option value="completed">Completed</option>
                      <option value="canceled">Canceled</option>
                      <option value="no-show">No Show</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2 text-text-primary/70">
                    Service Title
                  </label>
                  <input
                    type="text"
                    value={formData.service_title}
                    onChange={(e) => setFormData({ ...formData, service_title: e.target.value })}
                    placeholder="e.g., Math Tutoring Session"
                    className="w-full px-4 py-2.5 neu rounded-xl text-text-primary bg-background focus:outline-none focus:ring-2 focus:ring-accent/30"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-2 text-text-primary/70">
                      Start Time *
                    </label>
                    <input
                      type="datetime-local"
                      value={formData.scheduled_start}
                      onChange={(e) => setFormData({ ...formData, scheduled_start: e.target.value })}
                      className="w-full px-4 py-2.5 neu rounded-xl text-text-primary bg-background focus:outline-none focus:ring-2 focus:ring-accent/30"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2 text-text-primary/70">
                      End Time *
                    </label>
                    <input
                      type="datetime-local"
                      value={formData.scheduled_end}
                      onChange={(e) => setFormData({ ...formData, scheduled_end: e.target.value })}
                      className="w-full px-4 py-2.5 neu rounded-xl text-text-primary bg-background focus:outline-none focus:ring-2 focus:ring-accent/30"
                      required
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2 text-text-primary/70">
                    Meeting URL
                  </label>
                  <input
                    type="url"
                    value={formData.meeting_url}
                    onChange={(e) => setFormData({ ...formData, meeting_url: e.target.value })}
                    placeholder="https://zoom.us/j/..."
                    className="w-full px-4 py-2.5 neu rounded-xl text-text-primary bg-background focus:outline-none focus:ring-2 focus:ring-accent/30"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2 text-text-primary/70">
                    Notes
                  </label>
                  <textarea
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    rows={3}
                    className="w-full px-4 py-2.5 neu rounded-xl text-text-primary bg-background focus:outline-none focus:ring-2 focus:ring-accent/30 resize-none"
                  />
                </div>
              </div>

              <div className="flex justify-end gap-4 mt-6">
                <motion.button
                  onClick={() => setIsFormOpen(false)}
                  className="px-4 py-2.5 neu rounded-xl font-medium text-text-primary/70 hover:text-text-primary"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  Cancel
                </motion.button>
                <motion.button
                  onClick={handleSave}
                  disabled={saving}
                  className="px-4 py-2.5 neu rounded-xl font-semibold text-text-primary neu-hover bg-accent/10 hover:bg-accent/20 disabled:opacity-50"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  {saving ? 'Saving...' : 'Save'}
                </motion.button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

